import moment from 'moment-timezone';
import { faker } from '@faker-js/faker';
import visitorsClassicScenario from './visitors-classic';

/**
 * Sets up Default Mirage Scenario
 *
 * @param {object} server Mirage Server
 */
export default function (server) {
  /*
    Seed your development database using your factories.
    This data will not be loaded in your tests.

    Make sure to define a factory for each model you want to create.
  */

  // NOTE: If you are looking for how we auth the user for mirage,
  // it's done via an initializer `app/initializers/auth-current-user-in-mirage.js`

  const {
    company,
    locations: [SFLocation, NYLocation],
    subscription: vrSubscription,
    deliveriesSubscription,
  } = visitorsClassicScenario(server, {
    withDeliveriesSubscription: true,
    withPlans: true,
    withSubscriptionPlans: true,
    withVisitorsLocationSubscription: true,
    withRoomsLocationSubscription: true,
    withPrinter: true,
    withBadges: true,
    withAgreementPages: true,
    withPhotoPages: true,
    withSummaryPages: true,
    withIdScanPages: true,
    withBlacklistFilter: true,
  });

  const scimIntegration = server.create('scim-integration');
  company.update({ planIntent: 'enterprise', plan: 'enterprise', period: 'yearly', scimIntegration });
  server.create('company-role', 'asEmployee', {
    company,
    user: server.schema.users.first(),
  });

  // Deliveries Subscription
  deliveriesSubscription.update({
    couponPercentOff: 20,
    trialEndDate: moment().add(5, 'days').toDate(),
    isManual: false,
    onTrial: false,
    plan: 'standard',
  });
  // VR Subscription
  vrSubscription.update({
    trialEndDate: null,
    last4: '1234',
    expirymonth: 10,
    expiryyear: 2050,
    brand: 'Visa',
    plan: 'enterprise',
    period: 'yearly',
    couponPercentOff: 10,
    features: [
      'pre_registration_required',
      'billing',
      'sign_in_visitors',
      'custom_notifications',
      'group_sign_in',
      'nda_enabled',
      'visitor_photos',
      'visitor_badge_printing',
      'sms_notification',
      'slideshows',
      'custom_badge',
      'presign_nda',
      'multiple_visitor_types',
      'sign_in_from_invite',
      'slack',
      'analytics',
      'assistants',
      'blacklist',
      'locations_grouping',
      'global_analytics',
      'rooms-calendar-permissions',
      'conditional_sign_in_fields',
      'visitor_survey',
      'welcome_guide',
    ],
  });

  server.schema.badges.all().update({
    enabled: false,
  });
  // Employees
  // server.createList('employee', 5, {
  //   company,
  //   locationIds: [SFLocation.id, NYLocation.id],
  // });

  // Workplace subscription
  server.create('subscription', {
    app: 'empxp',
    companyId: company.id,
    features: ['occupancy_analytics'],
    trialEndDate: moment().add(5, 'days').toDate(),
  });
  // Rooms subscription
  server.create('subscription', { app: 'rooms', companyId: company.id, features: ['check_in_notification'] });
  // Desks subscription
  server.create('subscription', 'asDesksOnTrial', { companyId: company.id });
  server.create('desk-location', { companyId: company.id, locationId: SFLocation.id });

  server.create('notification-config', { location: SFLocation, notificationReminderInterval: 'daily' });

  // Features API
  server.create('feature', {
    company_id: company.id,
    features: ['test_feature_1', 'test_feature_2'],
  });

  // Set default notifications
  SFLocation.update({
    multipleLanguagesEnabled: true,
    enabledLocales: ['en', 'fr'],
    preRegistrationEnabled: true,
    currentVersion: '11.2.6',
    customNotifications: [],
    defaultNotifications: [
      {
        for: 'individual',
        'delegated-message': '%{visitor_name} is here to see %{host_name} at %{company_name}.',
        'host-message': '%{visitor_name} is here to see you at %{company_name}.',
        message: '%{visitor_name} is here to see no host at %{company_name}.',
      },
      {
        for: 'bulk',
        'delegated-message':
          'A group of %{number_of_visitors} visitors are here to see %{host_name} at %{company_name} %{location_name}.',
        'host-message':
          'A group of %{number_of_visitors} visitors are here to see you at %{company_name} %{location_name}.',
        message:
          'A group of %{number_of_visitors} visitors are here to see no host at %{company_name} %{location_name}.',
      },
    ],
  });

  // Versions for changelog
  const [user] = server.createList('user', 4, 'withChangelog');

  // SCIM create changelog
  server.create('changelog', {
    user,
    event: 'create',
    items: [{ type: 'scim_integrations', field: 'name', value: [null, 'okta'] }],
  });

  // SCIM change from all users to per-location filterchangelog
  server.create('changelog', {
    user,
    event: 'update',
    items: [
      {
        type: 'scim_integrations',
        field: 'filterRules',
        value: [
          { data: [], type: 'locations' },
          {
            data: [
              {
                name: 'city',
                label: 'City',
                value: 'San Francisco',
                operation: '=',
                'location-id': SFLocation.id,
                location_id: SFLocation.id,
              },
            ],
            type: 'locations',
          },
        ],
      },
    ],
  });

  // SCIM modifying location filter + add another location filter changelog
  server.create('changelog', {
    user,
    event: 'update',
    items: [
      {
        type: 'scim_integrations',
        field: 'filterRules',
        value: [
          {
            data: [
              {
                name: 'city',
                label: 'City',
                value: 'San Francisco',
                operation: '=',
                'location-id': SFLocation.id,
                location_id: SFLocation.id,
              },
            ],
            type: 'locations',
          },
          {
            data: [
              {
                name: 'city',
                label: 'City',
                value: 'San Francisco',
                operation: '=~',
                'location-id': SFLocation.id,
                location_id: SFLocation.id,
              },
              {
                name: 'locale',
                label: 'Locale',
                value: 'US',
                operation: '=',
                'location-id': SFLocation.id,
                location_id: SFLocation.id,
              },
              {
                name: 'city',
                label: 'City',
                value: 'New York',
                operation: '=~',
                'location-id': NYLocation.id,
                location_id: NYLocation.id,
              },
            ],
            type: 'locations',
          },
        ],
      },
    ],
  });

  // SCIM switching from per location filter to sync all users changelog
  server.create('changelog', {
    user,
    event: 'update',
    items: [
      {
        type: 'scim_integrations',
        field: 'filterRules',
        value: [
          {
            data: [
              {
                name: 'city',
                label: 'City',
                value: 'San Francisco',
                operation: '=',
                'location-id': SFLocation.id,
                location_id: SFLocation.id,
              },
            ],
            type: 'locations',
          },
          { data: [], type: 'locations' },
        ],
      },
    ],
  });

  // SCIM regenerating API token changelog
  server.create('changelog', {
    user,
    event: 'update',
    items: [{ type: 'scim_integrations', field: 'accessTokenId', value: [390, 391] }],
  });

  // SCIM disconnecting changelog
  server.create('changelog', {
    user,
    event: 'destroy',
    items: [{ type: 'scim_integrations', field: 'name', value: ['okta', null] }],
  });

  // Create 3 SCIM groups
  server.create('employee-group', { displayName: 'SCIM Group 1' });
  server.create('employee-group', { displayName: 'SCIM Group 2' });
  server.create('employee-group', { displayName: 'SCIM Group 3' });

  // Default deliver area
  const deliveryArea = server.create('delivery-area', { location: SFLocation, locationName: SFLocation.name });
  // Deliveries Guide
  server.create('setup-guide-step', { title: 'Record your first delivery' });
  SFLocation.update({ deliveriesOnboardingComplete: true });

  // Default iPads
  const OS_VERSIONS = ['11.2.6', '11.3.1', '10.0.2', '11.2.5', '10.1.2'];
  // Always connected iPads
  server.createList('device', 2, {
    location: SFLocation,
    lastHeartbeatAt: new Date(),
    osVersion: '11.2.6',
    ipAddress: '192.168.7.61',
    maximumOsVersion: '11.2.6',
  });
  // Random connected iPads
  server.createList('device', 5, {
    location: SFLocation,
    maximumOsVersion: '11.2.6',
    lastHeartbeatAt: () => faker.helpers.arrayElement([new Date(), null]),
    osVersion: () => faker.helpers.arrayElement(OS_VERSIONS),
  });

  // Visitor Guide
  SFLocation.update({ visitorsOnboardingComplete: true });
  const location = SFLocation;
  const visitorGuide = server.create('visitor-guide', { location });
  const range = Array.from(Array(5).keys());
  range.forEach((position) => {
    server.create('visitor-guide-section', { visitorGuide, position });
  });

  // Rooms setup
  const [roomA, roomB] = server.createList('room', 10, {
    calendarReadPermission: (i) => i % 6 !== 0,
    calendarWritePermission: (i) => i % 6 !== 0,
  });
  server.create('room-device', { room: roomA });
  server.create('room-device', { room: roomB });
  server.create('connection', { company });
  server.create('room-location-config');
  server.createList('room-device', 5);

  server.schema.companyConfigs.first().update({
    hasConnectedCalendarAccount: true,
    provider: 'google',
    connectedEmailAccount: 'foo@bar.com',
  });

  const rDiaz = server.create('user', {
    name: 'Rosa Diaz',
    fullName: 'Rosa Diaz',
    email: 'rosa.diaz@the99.gov',
    locationId: SFLocation.id,
  });

  server.create('location-role', {
    roleName: 'Receptionist',
    userId: rDiaz.id,
    locationId: SFLocation.id,
  });

  // Blacklist filters
  server.createList('blacklist-filter', 100, { location });
  const signInFieldPage = server.schema.signInFieldPages.first();

  server.create('signInField', {
    signInFieldPageId: signInFieldPage.id,
    kind: 'phone',
    name: 'Your Phone Number',
    localized: 'Your Phone Number',
    identifier: '',
    required: false,
    position: 3,
  });

  server.create('entry', {
    location,
    userData: [
      { field: 'Purpose of visit', value: 'random entry flow' },
      { field: 'Host', value: faker.person.fullName() },
      { field: 'Your Phone Number', value: '+573006567644' },
    ],
    email: faker.internet.email(),
  });

  server.create('invite', {
    location,
    userData: [
      { field: 'Purpose of visit', value: 'random invite flow' },
      { field: 'Host', value: faker.person.fullName() },
      { field: 'Your Phone Number', value: '+573006567655' },
    ],
    email: faker.internet.email(),
  });

  // Entries
  server.create('entry', { location: SFLocation, approvalStatus: { status: 'approved' } });
  server.create('entry', { location: SFLocation, approvalStatus: { status: 'denied' } });

  // Invites
  server.create('invite', { location: SFLocation, approvalStatus: { status: 'denied' } });
  server.create('invite', { location: SFLocation, approvalStatus: { status: 'approved' } });

  // Announcements
  server.createList('announcement', 5, { company, location, numSeen: faker.number.int(100) });

  // Desks
  const floor1 = server.create('floor', {
    location: SFLocation,
    floorPlanUrl: '/assets/images/features/avatar-placeholder.png',
  });

  const floor2 = server.create('floor', {
    location: SFLocation,
    floorPlanUrl: '/assets/images/features/avatar-placeholder.png',
  });
  server.createList('desk', 5, 'asUnplaced', { floor: floor1 });
  server.createList('desk', 25, { floor: floor2 });

  // Flow
  server.create('flow', 'withES', 'withQuestions', { location: SFLocation });

  // Visitor entrance
  server.create('visitor-entrance', { location });

  // Scim Groups
  const scimGroup = server.create('scim-group');
  // Neighborhoods
  server.create('neighborhood', {
    location,
    scimDivisions: ['engines'],
    scimGroupIds: [scimGroup.id],
  });
  server.createList('neighborhood', 3, { location });

  const inviteTemplate = server.create('mailer-template', 'asInviteEmail');

  server.create('mailer-template', {
    companyIds: [company.id],
    envoyDefault: false,
    envoyDefaultId: inviteTemplate.id,
  });

  server.create('mailer-template', {
    locationIds: [SFLocation.id],
    envoyDefault: false,
    envoyDefaultId: inviteTemplate.id,
  });

  server.create('mailer-template', {
    locationIds: [NYLocation.id],
    envoyDefault: false,
    envoyDefaultId: inviteTemplate.id,
  });

  const welcomeTemplate = server.create('mailer-template', 'asWelcomeEmail');

  server.create('mailer-template', {
    companyIds: [company.id],
    envoyDefault: false,
    envoyDefaultId: welcomeTemplate.id,
  });

  server.create('mailer-template', {
    locationIds: [SFLocation.id],
    envoyDefault: false,
    envoyDefaultId: welcomeTemplate.id,
  });

  server.create('mailer-template', {
    locationIds: [NYLocation.id],
    envoyDefault: false,
    envoyDefaultId: welcomeTemplate.id,
  });

  // Printers
  server.schema.printers.all().destroy();

  server.createList('printer', 2, {
    locationId: SFLocation.id,
  });

  server.create('printer', {
    locationId: SFLocation.id,
    status: 'not connected',
  });

  server.createList('printer', 1, {
    locationId: NYLocation.id,
  });

  server.createList('delivery', 3, {
    locationId: NYLocation.id,
    deliveryAreaId: deliveryArea.id,
  });

  server.create('meeting', 'withEmployee');

  const userDocumentTemplateVaccineCard = server.create('user-document-template', 'asCovidVaccineCard');
  const userDocumentTemplateTestResult = server.create('user-document-template', 'asCovidTestResult');

  server.create('user-document-template-configuration', {
    active: false,
    userDocumentTemplate: userDocumentTemplateVaccineCard,
    userDocumentTemplateConfigurableId: { id: SFLocation.id, type: 'location' },
  });

  server.create('user-document-template-configuration', {
    active: false,
    userDocumentTemplate: userDocumentTemplateVaccineCard,
    userDocumentTemplateConfigurableId: { id: NYLocation.id, type: 'location' },
  });

  server.create('user-document-template-configuration', {
    active: false,
    userDocumentTemplate: userDocumentTemplateTestResult,
    userDocumentTemplateConfigurableId: { id: SFLocation.id, type: 'location' },
  });

  server.create('user-document-template-configuration', {
    active: false,
    userDocumentTemplate: userDocumentTemplateTestResult,
    userDocumentTemplateConfigurableId: { id: NYLocation.id, type: 'location' },
  });

  server.create('ui-hook', {
    locationId: SFLocation.id,
  });

  server.create('ui-hook', {
    locationId: NYLocation.id,
  });

  const roombaCompany = server.create('roomba/company', 'withGoogleCalendar', {
    id: company.id,
  });
  const roombaLocation = server.create('roomba/location', { company: roombaCompany });
  server.createList('roomba/room', 3, 'withDevice', { company: roombaCompany });
  server.createList('roomba/room', 2, 'withDevice', { company: roombaCompany, location: roombaLocation });
}
